/**
 * Din datele etajului (cm, AP-uri, semnale, puncte de survey) in ce deseneaza
 * harta (pixeli, culori, etichete). Functii pure.
 */
import type { DeviceSignal, FloorAp, FloorItem, InventoryDevice, SurveySpot, Wall } from '../api/floorPlans'
import { spotValue, type BssidAp } from './coverage'
import { cmToPx } from './geometry'
import { step, type Sample, type Thresholds } from './idw'
import type { MapItem, MapSpot, MapWall } from './mapTypes'
import type { ApM, Material, WallM } from './predict'

export const COLORS = {
  good: '#2ea043',
  fair: '#c9b21c',
  poor: '#e8912d',
  bad: '#d23c3c',
  none: '#8a8f98',
  planned: '#3b7dd8',
} as const

export const MATERIAL_COLORS: Record<Material, string> = {
  drywall: '#9aa4b1',
  glass: '#5fb3d9',
  wood: '#a0703c',
  brick: '#b5523b',
  concrete: '#57606a',
  metal: '#24292f',
}

/** Culoarea unui semnal pe trepte (-67 / -72 / -80), gri fara semnal. */
export function signalColor(rssi: number | null | undefined, t: Thresholds): string {
  if (rssi === null || rssi === undefined || Number.isNaN(rssi)) return COLORS.none
  return COLORS[step(rssi, t)]
}

/**
 * Culoarea unui AP dupa experienta clientilor lui (AccessPoints: aceleasi
 * praguri): "Weak AP" rosu, "Sticky clients" portocaliu, fara clienti gri,
 * altfel verde. Un AP planificat e albastru, cu contur punctat.
 */
export function apLook(ap: FloorAp | undefined, planned: boolean): { color: string; ring: 'weak' | 'sticky' | null } {
  if (planned) return { color: COLORS.planned, ring: null }
  if (!ap || ap.clients === 0) return { color: COLORS.none, ring: null }
  if (ap.weak) return { color: COLORS.bad, ring: 'weak' }
  if (ap.sticky.length > 0) return { color: COLORS.poor, ring: 'sticky' }
  return { color: COLORS.good, ring: null }
}

/** AP-ul din lista firmei caruia ii apartine cheia salvata pe plan. */
export function apOf(key: string | null, aps: FloorAp[]): FloorAp | undefined {
  if (key === null) return undefined
  return aps.find((a) => a.key === key || a.keys.includes(key))
}

export function itemLabel(item: FloorItem, aps: FloorAp[], devices: InventoryDevice[]): string {
  if (item.kind === 'ap') {
    if (item.ap_group_key === null) return item.label || 'Planned AP'
    const ap = apOf(item.ap_group_key, aps)
    return item.label || ap?.name || item.ap_group_key
  }
  if (item.kind === 'device') return devices.find((d) => d.id === item.device_id)?.name ?? `Device ${item.device_id ?? ''}`
  return item.label ?? ''
}

export function mapItems(
  items: FloorItem[],
  scale: number,
  aps: FloorAp[],
  devices: InventoryDevice[],
  signals: Record<string, DeviceSignal>,
  t: Thresholds,
  selected: number | null,
): MapItem[] {
  return items.map((it, index) => {
    const base = { index, kind: it.kind, x: cmToPx(it.x_cm, scale), y: cmToPx(it.y_cm, scale), label: itemLabel(it, aps, devices), selected: selected === index }
    if (it.kind === 'ap') {
      const look = apLook(apOf(it.ap_group_key, aps), it.ap_group_key === null)
      return { ...base, color: look.color, ring: look.ring, planned: it.ap_group_key === null }
    }
    if (it.kind === 'device') {
      const s = it.device_id !== null ? signals[String(it.device_id)] : undefined
      const rssi = s?.rssi_dbm ?? null
      return { ...base, color: signalColor(rssi, t), label: rssi === null ? base.label : `${base.label} (${Math.round(rssi)} dBm)` }
    }
    return { ...base, color: COLORS.none }
  })
}

export function mapWalls(walls: Wall[], scale: number, selected: number | null): MapWall[] {
  return walls.map((w, index) => ({
    index,
    x1: cmToPx(w.x1, scale),
    y1: cmToPx(w.y1, scale),
    x2: cmToPx(w.x2, scale),
    y2: cmToPx(w.y2, scale),
    color: MATERIAL_COLORS[w.m] ?? MATERIAL_COLORS.drywall,
    selected: selected === index,
  }))
}

/** Punctele survey-ului: colorate dupa valoarea lor, `pending` goale. */
export function mapSpots(spots: SurveySpot[], scale: number, bssidAps: Record<string, BssidAp>, ap: 'best' | string, t: Thresholds, selected: number | null): MapSpot[] {
  return spots.map((s) => {
    const v = s.status === 'ok' ? spotValue(s.readings, bssidAps, ap) : null
    const pending = s.status === 'pending'
    return {
      id: s.id,
      x: cmToPx(s.x_cm, scale),
      y: cmToPx(s.y_cm, scale),
      pending,
      color: s.status === 'ok' ? signalColor(v, t) : s.status === 'pending' ? COLORS.none : COLORS.bad,
      label: pending ? '...' : v !== null ? `${Math.round(v)}` : s.status === 'ok' ? '-' : '!',
      selected: selected === s.id,
    }
  })
}

/** Masuratorile pentru IDW, in metri. */
export function surveySamples(spots: SurveySpot[], bssidAps: Record<string, BssidAp>, ap: 'best' | string): Sample[] {
  const out: Sample[] = []
  for (const s of spots) {
    if (s.status !== 'ok') continue
    const v = spotValue(s.readings, bssidAps, ap)
    if (v !== null) out.push({ x: s.x_cm / 100, y: s.y_cm / 100, v })
  }
  return out
}

/** AP-urile puse pe plan (reale si planificate) si peretii, in metri, pentru predictie. */
export function predictInput(items: FloorItem[], walls: Wall[]): { aps: ApM[]; walls: WallM[] } {
  return {
    aps: items
      .filter((i) => i.kind === 'ap')
      .map((i) => ({ x: i.x_cm / 100, y: i.y_cm / 100, tx_dbm: i.attrs?.tx_dbm ?? null, band: i.attrs?.band ?? null })),
    walls: walls.map((w) => ({ a: { x: w.x1 / 100, y: w.y1 / 100 }, b: { x: w.x2 / 100, y: w.y2 / 100 }, m: w.m })),
  }
}
